// FREEZE: YES
// Reason: Core settings service - Frontend API interface for system configuration
// Date: 2024-01-15
// Author: Admin

import { apiCall } from './api.service';

// Local cache for settings
const settingsCache = {
  data: {},
  ttl: 24 * 60 * 60 * 1000,
  timestamp: null
};

/**
 * Settings Manager Service
 * Provides methods to interact with clerk-specific settings
 * @see ../../backend/docs/Clerkspecific_settings.md for detailed documentation
 */
class SettingsManager {
  /**
   * Get all settings for a specific agent
   * @param {number|string} agentId - The ID of the agent/clerk
   * @returns {Promise<Object>} Object containing all settings for the agent
   * @throws {Error} If the settings cannot be retrieved
   * 
   * @example
   * const settings = await SettingsManager.getAgentSettings(123);
   * console.log(settings);
   * // {
   * //   setting1: { type: "boolean", value: true },
   * //   setting2: { type: "string", value: "example" }
   * // }
   */
  static async getAgentSettings(agentId) {
    try {
      const settingKey = `clerk_settings_${agentId}`;
      const response = await apiCall(`settings/${settingKey}`);
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to retrieve settings');
      }

      return response.setting?.extradata?.settings || {};
    } catch (error) {
      console.error('Error fetching agent settings:', error);
      throw error;
    }
  }

  /**
   * Get a specific setting value for an agent
   * @param {number|string} agentId - The ID of the agent/clerk
   * @param {string} settingName - The name of the setting to retrieve
   * @returns {Promise<any>} The value of the setting
   * @throws {Error} If the setting cannot be retrieved or doesn't exist
   * 
   * @example
   * const value = await SettingsManager.getSettingValue(123, "my_setting");
   * console.log(value); // The value of my_setting
   */
  static async getSettingValue(agentId, settingName) {
    try {
      const settings = await this.getAgentSettings(agentId);
      
      if (!settings[settingName]) {
        throw new Error(`Setting '${settingName}' not found for agent ${agentId}`);
      }

      return settings[settingName].value;
    } catch (error) {
      console.error(`Error fetching setting '${settingName}':`, error);
      throw error;
    }
  }

  /**
   * Check if a setting exists for an agent
   * @param {number|string} agentId - The ID of the agent/clerk
   * @param {string} settingName - The name of the setting to check
   * @returns {Promise<boolean>} True if the setting exists
   */
  static async hasSetting(agentId, settingName) {
    try {
      const settings = await this.getAgentSettings(agentId);
      return !!settings[settingName];
    } catch (error) {
      return false;
    }
  }

  /**
   * Get the type of a specific setting
   * @param {number|string} agentId - The ID of the agent/clerk
   * @param {string} settingName - The name of the setting
   * @returns {Promise<string|null>} The type of the setting or null if not found
   */
  static async getSettingType(agentId, settingName) {
    try {
      const settings = await this.getAgentSettings(agentId);
      return settings[settingName]?.type || null;
    } catch (error) {
      console.error(`Error fetching setting type for '${settingName}':`, error);
      return null;
    }
  }

  /**
   * Get a global system setting
   * @param {string} settingName - The name of the setting to retrieve
   * @param {any} defaultValue - Default value if setting not found
   * @param {boolean} forceRefresh - Force refresh cache
   * @returns {Promise<any>} The value of the setting
   */
  static async getGlobalSetting(settingName, defaultValue = null, forceRefresh = false) {
    try {
      // Check cache first
      if (!forceRefresh && settingsCache.timestamp && 
          (Date.now() - settingsCache.timestamp < settingsCache.ttl) &&
          settingName in settingsCache.data) {
        return settingsCache.data[settingName];
      }

      const response = await apiCall(`settings/${settingName}`);
      
      if (!response.success) {
        console.warn(`Setting '${settingName}' not found, using default:`, defaultValue);
        return defaultValue;
      }

      const value = response.setting?.value !== undefined ? response.setting.value : defaultValue;
      
      // Update cache
      if (!settingsCache.timestamp || (Date.now() - settingsCache.timestamp >= settingsCache.ttl)) {
        settingsCache.data = {};
        settingsCache.timestamp = Date.now();
      }
      settingsCache.data[settingName] = value;
      
      return value;
    } catch (error) {
      console.error(`Error fetching global setting '${settingName}':`, error);
      return defaultValue;
    }
  }

  /**
   * Get multiple global system settings
   * @param {Array<string>} settingNames - Array of setting names to retrieve
   * @param {Object} defaults - Object with default values for each setting
   * @param {boolean} forceRefresh - Force refresh cache
   * @returns {Promise<Object>} Object containing all requested settings
   */
  static async getMultipleGlobalSettings(settingNames, defaults = {}, forceRefresh = false) {
    try {
      // Return from cache if all requested keys are present and TTL hasn't expired
      if (!forceRefresh && settingsCache.timestamp &&
          (Date.now() - settingsCache.timestamp < settingsCache.ttl) &&
          settingNames.every(name => name in settingsCache.data)) {
        const cached = {};
        settingNames.forEach(name => { cached[name] = settingsCache.data[name]; });
        return cached;
      }

      // Use bulk endpoint for better performance (single HTTP request instead of multiple)
      try {
        const response = await apiCall('settings/bulk', 'POST', {
          names: settingNames,
          defaults: defaults
        });
        
        if (response.success && response.values) {
          // Update cache if not forcing refresh
          if (!forceRefresh && response.values) {
            if (!settingsCache.timestamp || (Date.now() - settingsCache.timestamp >= settingsCache.ttl)) {
              settingsCache.data = {};
              settingsCache.timestamp = Date.now();
            }
            Object.keys(response.values).forEach(name => {
              settingsCache.data[name] = response.values[name];
            });
          }
          
          return response.values;
        }
      } catch (bulkError) {
        // Fallback to individual requests if bulk endpoint fails
        console.warn('Bulk settings endpoint failed, falling back to individual requests:', bulkError);
      }
      
      // Fallback: Get each setting individually (for backward compatibility)
      const result = {};
      for (const settingName of settingNames) {
        const defaultValue = defaults[settingName] !== undefined ? defaults[settingName] : null;
        result[settingName] = await this.getGlobalSetting(settingName, defaultValue, forceRefresh);
      }
      
      return result;
    } catch (error) {
      console.error('Error fetching multiple global settings:', error);
      // Return defaults if everything fails
      return { ...defaults };
    }
  }

  /**
   * Update a global system setting
   * @param {string} settingName - The name of the setting to update
   * @param {any} value - The new value
   * @param {string} type - The type of the setting
   * @param {Object} extradata - Additional data
   * @returns {Promise<boolean>} True if successful
   */
  static async updateGlobalSetting(settingName, value, type = 'string', extradata = null) {
    try {
      const response = await apiCall(`settings/${settingName}`, 'PUT', {
        value,
        type,
        extradata
      });
      
      if (response.success) {
        // Clear cache for this setting
        if (settingsCache.data[settingName] !== undefined) {
          delete settingsCache.data[settingName];
        }
        return true;
      }
      
      return false;
    } catch (error) {
      console.error(`Error updating global setting '${settingName}':`, error);
      return false;
    }
  }
}

/**
 * Settings service object with methods expected by the application
 */
const settingsService = {
  // Global settings methods (used by Sentry, useSettings, etc.)
  getSetting: (settingName, defaultValue = null, forceRefresh = false) => 
    SettingsManager.getGlobalSetting(settingName, defaultValue, forceRefresh),
    
  getMultiple: (settingNames, defaults = {}, forceRefresh = false) => 
    SettingsManager.getMultipleGlobalSettings(settingNames, defaults, forceRefresh),
    
  updateSetting: (settingName, value, type = 'string', extradata = null) => 
    SettingsManager.updateGlobalSetting(settingName, value, type, extradata),

  // Agent-specific settings methods (for backward compatibility)
  getAgentSettings: (agentId) => SettingsManager.getAgentSettings(agentId),
  getSettingValue: (agentId, settingName) => SettingsManager.getSettingValue(agentId, settingName),
  hasSetting: (agentId, settingName) => SettingsManager.hasSetting(agentId, settingName),
  getSettingType: (agentId, settingName) => SettingsManager.getSettingType(agentId, settingName),
};

// Export both the class (for direct use) and the service object (for compatibility)
export { SettingsManager };
export default settingsService; 